import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import { OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CustomApplicationState from "@WhiteCap/Store/Data/CustomApplicationState/CustomApplicationState";
import React, { useContext } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomApplicationState) => {
    const location = getLocation(state);
    const parsedQuery = parseQueryString<{
        orderId?: string;
    }>(location.search);
    const orderNumber = parsedQuery.orderId ?? parsedQuery.orderId;

    return {
        orderData: orderNumber ? state.WhiteCap.customOrderStatusPage.orderStatusById[orderNumber] : null,
        pageType: getCurrentPage(state).type,
    };
};

type Props = ReturnType<typeof mapStateToProps>;

export interface OrderDetailsShippingAddressStyles {
    titleText?: TypographyProps;
    addressDisplay?: AddressInfoDisplayStyles;
    wrapper?: InjectableCss;
}

export const shippingAddressStyles: OrderDetailsShippingAddressStyles = {
    wrapper: {
        css: css`
            padding: 5px 24px;
            padding-bottom: 10px;
            width: 100%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 5px 14px;
                    `,
                    css`
                        padding: 5px 14px;
                    `,
                    css``,
                    css``,
                    css``,
                ])}
        `,
    },
    titleText: {
        variant: "h3",
        css: css`
            @media print {
                font-size: 12px;
            }
            margin-bottom: 24px;
            font-size: 16px;
            padding-top: 10px;
            font-weight: 500;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 14px;
                        margin-bottom: 0px;
                        padding-top: 0;
                    `,
                    css`
                        font-size: 14px;
                        margin-bottom: 0px;
                        padding-top: 0;
                    `,
                    css``,
                    css``,
                    css``,
                ])}
        `,
    },
};

const styles = shippingAddressStyles;

const OrderDetailsShippingAddress: React.FunctionComponent<Props> = ({ pageType, orderData }) => {
    const order = pageType === "CustomOrderStatusPage" ? orderData : useContext(OrderStateContext).value;
    if (!order) {
        return null;
    }

    const hasShippingAddress = order.fulfillmentMethod === FulfillmentMethod.Ship || !order.fulfillmentMethod;

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="orderDetailShippingInformation">
            <Typography {...styles.titleText}>
                {translate(hasShippingAddress ? "SHIP-TO" : "Pick Up Location")}
            </Typography>
            <AddressInfoDisplay
                companyName={order.stCompanyName}
                address1={order.stAddress1}
                address2={order.stAddress2}
                address3={order.stAddress3}
                address4={order.stAddress4}
                city={order.shipToCity}
                state={order.shipToState}
                postalCode={order.shipToPostalCode}
                country={order.stCountry}
                extendedStyles={styles.addressDisplay}
            />
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(OrderDetailsShippingAddress),
    definition: {
        allowedContexts: ["OrderDetailsPage", "VmiOrderDetailsPage", "CustomOrderStatusPage"],
        group: "Order Details",
    },
};

export default widgetModule;
